// Libraries
import { MouseEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import moment from 'moment';
import { useLocation } from 'react-router-dom';

// MUI
import { Button, Container } from '@mui/material';
import { ButtonGroup } from '@mui/material';
import Grid from '@mui/material/Grid';

// Core
import { paramRepository, ResourceTextApplication } from '../../core/resources';
import utils from '../../core/utils';

// Interfaces
import {
    IProductionPlan,
    IProductionPlanUserAccess,
    IProductionPlanSummary,
    IFarmSupplier,
    IProductionPlanNotificationLocationData,
} from '../../interfaces/IProductionPlan';
import { IFarmDetails } from '../../interfaces/IBusinessEntities';

// Store
import { useAppSelector } from '../../store/hooks';

// API
import productionPlanApi from '../../api/productionPlanApi';
import companyApi from '../../api/companyApi';

// Common
import { ViewTitle } from '../../common/typography';
import ViewLoader from '../../common/ViewLoader';
import ADefaultButton from '../../common/buttons/ADefaultButton';
import { Context } from '../../common/contextSelector/ContextSelector';

// Feature
import ProductionPlan from './ProductionPlan';
import ProductionPlanSummaryDataGrid from './data-grid/ProductionPlanSummaryDataGrid';
import { canCreate, canUpdate, isAdminUser } from './helpers/ProductionPlanAccessRights';
import '../shared/styles/sharedStyles.scss';
import { ARadioButtons } from '../../common/inputs';
import FormValidator from '../../core/FormValidator';

export default function ProductionPlanList() {
    const { t } = useTranslation<ResourceTextApplication[]>([
        'AnelmaBovine',
        'AnelmaLayout',
        'AnelmaCattleProductionPlan',
        'AnelmaCompany',
    ]);
    const { enqueueSnackbar } = useSnackbar();

    const context = useAppSelector((state) => state.context);
    const location = useLocation();

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [selectedPlan, setSelectedPlan] = useState<IProductionPlan | undefined | null>(null);
    const [planIsReadOnly, setPlanIsReadOnly] = useState<boolean>(false);
    const [farmDetails, setFarmDetails] = useState<IFarmDetails>();
    const [planSummaries, setPlanSummaries] = useState<IProductionPlanSummary[] | null>(null);
    const [userAccess, setUserAccess] = useState<IProductionPlanUserAccess | undefined | null>(
        null
    );
    const [canCreatePlan, setCanCreatePlan] = useState<boolean>(false);
    const [productionPlanStart, setProductionPlanStart] = useState<Date>();
    const [locationData, setLocationData] = useState<IProductionPlanNotificationLocationData>(
        initializeLocationData()
    );
    const [isNew, setIsNew] = useState<boolean>(false);
    const [productionPlanSwitchSelection, setProductionPlanSwitchSelection] = useState<number>(0);

    useEffect(() => {
        paramRepository.load(['AnelmaBovine']).then(() => {
            const ppStart = paramRepository.resource('AnelmaBovine', 'ProductionPlanStart');
            if (ppStart && ppStart.length > 0) {
                let sorted = ppStart.sort((a, b) => a.code.localeCompare(b.code)); 
                setProductionPlanStart(new Date(sorted[sorted.length - 1].code));
            }
        });

        setUserAccess({
            IsAdmin: isAdminUser(),
            CanUpdate: canUpdate(),
            CanCreate: canCreate(),
        });
    }, []);

    useEffect(() => {
        if (context.data.currentContext) {
            initializeFarmDetails(context.data.currentContext);
        } else setFarmDetails(undefined);
    }, [context]);

    useEffect(() => {
        if (farmDetails && farmDetails.Id) {
            getFarmProductionPlans();
        }
    }, [farmDetails]);

    useEffect(() => {
        setCanCreatePlan(checkCanCreatePlan());
    }, [planSummaries, userAccess, farmDetails]);

    useEffect(() => {
        if (canCreatePlan && locationData.DisplayProductionPlanFormInstantly) createNewPlan();
    }, [canCreatePlan]);

    /** Cannot create plan when:
     *  - logged in user is producer user
     *  - selected farm is inactivated
     *  - there are already production plan for the selected farm for the target year
     *
     *  Additional information from JIRA ticket: http://jira.mtech.fi/browse/AN-1771
     */
    const checkCanCreatePlan = () => {
        if (!userAccess) return false;
        if ((userAccess.IsAdmin || userAccess.CanCreate) && planSummaries && farmDetails) {
            if (utils.isFarmInactivated(farmDetails))
                return false;

            const now = new Date().getTime();

            if (productionPlanStart) {
                let targetYear = productionPlanStart.getFullYear();

                if (productionPlanStart.getTime() < now)
                    targetYear = productionPlanStart.getFullYear() + 1;

                if (planSummaries.some(
                    (_) =>
                        (farmDetails.Id as string) === _.FarmId &&
                        _.TargetYear === targetYear
                )) {
                    return false;
                }
            }

            return true;
        }

        return false;
    };

    const initializeFarmDetails = (context: Context) => {
        setIsLoading(true);
        const companyByContext = companyApi
            .getCompanyByContext(context.context)
            .then((response) => {
                setFarmDetails(response?.Entity as IFarmDetails);
                setProductionPlanSwitchSelection(1);
            });
        Promise.all([companyByContext]).then(() => {
            setIsLoading(false);
        });
    };

    const createNewPlan = () => {
        if (!canCreatePlan) return;
        if (farmDetails && farmDetails.Id) {
            if (farmDetails.ProductionLines.length === 0) {
                enqueueSnackbar(t('AnelmaCompany:1122'), {
                    variant: 'error',
                });
                return;
            }

            setIsLoading(true);
            productionPlanApi
                .getTemplate(farmDetails.Id)
                .then((response) => {
                    setIsLoading(false);
                    if (!response) {
                        enqueueSnackbar(t('AnelmaCattleProductionPlan:1085'), {
                            variant: 'error',
                        });
                        return;
                    }

                    setPlanIsReadOnly(false);
                    setIsNew(true);
                    setSelectedPlan(response.Entity);
                });
        }
    };

    const openPlan = (item: IProductionPlanSummary, readonly: boolean) => {
        setIsLoading(true);
        productionPlanApi.getPlan(item.Id).then((response) => {
            setIsLoading(false);
            if (!response) {
                enqueueSnackbar(t('AnelmaCattleProductionPlan:1085'), {
                    variant: 'error',
                });
                return;
            }
            setPlanIsReadOnly(readonly);
            setIsNew(false);
            setSelectedPlan(response.Entity);
        });
    };

    const deletePlan = (item: IProductionPlanSummary) => {
        if (!planSummaries) return;
        setIsLoading(true);
        productionPlanApi.deletePlan(item.Id).then((response) => {
            setIsLoading(false);
            if (!response) {
                enqueueSnackbar(t('AnelmaCattleProductionPlan:1086'), {
                    variant: 'error',
                });
                return;
            }
            setPlanSummaries([...planSummaries.filter((x) => x.Id !== item.Id)]);
        });
    };

    function initializeLocationData() {
        return location.state !== null
            ? location.state.options
            : { DisplayProductionPlanFormInstantly: false };
    }

    const getFarmProductionPlans = () => {
        if (farmDetails && farmDetails.Id) {
            setIsLoading(true);
            productionPlanApi.getPlans(farmDetails.Id).then((response) => {
                if (!response) {
                    enqueueSnackbar(t('AnelmaCattleProductionPlan:1085'), {
                        variant: 'error',
                    });
                    return;
                }
                setPlanSummaries(response.Items);
                setIsLoading(false);
            });
        }
    };

    const getAllProductionPlans = () => {
        if (userAccess?.IsAdmin) {
            setIsLoading(true);
            productionPlanApi.getAllPlans().then((response) => {
                if (!response) {
                    enqueueSnackbar(t('AnelmaCattleProductionPlan:1085'), {
                        variant: 'error',
                    });
                    return;
                }
                setPlanSummaries(response.Items);
                setIsLoading(false);
            });
        }
    };

    const getProductionPlans = (value: number, reload: boolean) => {
        switch (value) {
            case 1:
                if (productionPlanSwitchSelection !== 1 && reload === false) {
                    setProductionPlanSwitchSelection(1);
                    getFarmProductionPlans();
                } else if (reload) {
                    getFarmProductionPlans();
                }
                break;
            case 2:
                if (productionPlanSwitchSelection !== 2 && reload === false) {
                    setProductionPlanSwitchSelection(2);
                    getAllProductionPlans();
                } else if (reload) {
                    getAllProductionPlans();
                }
                break;
            default:
                break;
        }
    };

    const getProductionPlanSwitchSelectionItems = () => {
        return [
            { text: t('AnelmaCattleProductionPlan:1091'), value: 1 },
            { text: t('AnelmaCattleProductionPlan:1092'), value: 2 },
        ];
    };

    if (!userAccess || !farmDetails) return <ViewLoader />;
    return (
        <Container id='anelma-animals-production-plan-list-container'>
            <ViewTitle>{t('AnelmaLayout:1075')}</ViewTitle>
            {userAccess?.IsAdmin && (
                <Grid container justifyContent='flex-start'>
                    <ButtonGroup variant='text'>
                        <ARadioButtons
                            id={'anelma-animals-production-plan-farm-switch'}
                            items={getProductionPlanSwitchSelectionItems()}
                            onChange={(v) => getProductionPlans(v as number, false)}
                            validator={new FormValidator()}
                            value={productionPlanSwitchSelection}
                            disabled={isLoading === true}
                        />
                    </ButtonGroup>
                </Grid>
            )}
            {userAccess.CanCreate && (
                <Grid container direction='row'>
                    <Grid size={{ xs: 8, sm: 8, md: 8, lg: 8 }}></Grid>
                    <Grid
                        className='animal-announcement-button-content-position'
                        size={{ xs: 4, sm: 4, md: 4, lg: 4 }}
                    >
                        <ADefaultButton
                            onClick={createNewPlan}
                            children={t('AnelmaCattleProductionPlan:1052')}
                            disabled={!canCreatePlan || isLoading}
                        ></ADefaultButton>
                    </Grid>
                </Grid>
            )}
            {(planSummaries && (
                <ProductionPlanSummaryDataGrid
                    data={planSummaries}
                    access={userAccess}
                    isLoading={isLoading}
                    onPlanOpen={openPlan}
                    onPlanDelete={deletePlan}
                />
            )) || <ViewLoader />}
            {selectedPlan && (
                <ProductionPlan
                    data={selectedPlan}
                    access={userAccess}
                    farmDetails={!userAccess.IsAdmin ? farmDetails : null}
                    readonly={planIsReadOnly}
                    onClose={(reload) => {
                        setSelectedPlan(null);
                        if (reload) getProductionPlans(productionPlanSwitchSelection, reload);
                        if (locationData.DisplayProductionPlanFormInstantly)
                            setLocationData({ DisplayProductionPlanFormInstantly: false });
                    }}
                    isNew={isNew}
                />
            )}
        </Container>
    );
}
